import { MODE_STRONG } from "../constants";
import { fetchHistory, type HistoryEntry, uploadReceipts } from "../sync";
import { escapeHTML, REASON_COPY, type RefusalReason } from "../ui";

const INTRO =
  "Aquí aparecen las verificaciones que llegaron a la UH. Un teléfono que verificó sin conexión y no ha vuelto a conectarse todavía no las ha enviado.";

/** With the year, unlike `formatWhen`: a history reaches back months. */
const when = (seconds: number) =>
  new Date(seconds * 1000).toLocaleString("es", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

/** The refusal in the words the verifier was shown, or the identifier itself
 *  for a reason a newer wallet wrote and this one has no words for. */
const outcome = (result: string) =>
  result === "ok" ? "Verificada" : REASON_COPY[result as RefusalReason] ?? result;

function entryHTML(entry: HistoryEntry): string {
  const who = `${escapeHTML(entry.other_name)} (${escapeHTML(entry.other_uid)})`;
  const line = entry.direction === "verified-me" ? `Te verificó ${who}` : `Verificaste a ${who}`;
  const strong = entry.mode === MODE_STRONG ? " · Modo reforzado" : "";
  return `<li class="entry ${entry.result === "ok" ? "good" : "bad"}"
              data-direction="${escapeHTML(entry.direction)}">
    <strong>${line}</strong><br>
    <span>${escapeHTML(when(entry.at))}${strong}</span> ·
    <span class="outcome">${escapeHTML(outcome(entry.result))}</span></li>`;
}

/**
 * Both directions of this holder's history, as the issuer has it. Nothing is
 * kept on the phone: the issuer is the one place both sides of a verification
 * meet, so offline there is nothing honest to show.
 */
export async function renderHistory(root: HTMLElement): Promise<() => void> {
  // A slow network must not paint this screen over whichever one the holder
  // moved to while it was waiting.
  let gone = false;
  const paint = (body: string) => {
    if (!gone) root.innerHTML = `<p class="intro">${escapeHTML(INTRO)}</p>${body}`;
  };

  const load = async () => {
    if (!navigator.onLine) {
      paint(`<p class="warn" id="history-offline">Necesitas conexión para ver tu historial.</p>`);
      return;
    }
    paint(`<p id="history-loading">Cargando…</p>`);
    // This phone's own receipts first, so the ones it verified since the last
    // sync are in the answer rather than an hour behind it.
    await uploadReceipts().catch(() => undefined);
    const answer = await fetchHistory();
    if (!answer.ok) {
      paint(answer.failure === "offline"
        ? `<p class="warn" id="history-offline">Necesitas conexión para ver tu historial.</p>`
        : `<p class="warn">La UH no pudo darte tu historial ahora. Inténtalo más tarde.</p>`);
      return;
    }
    paint(answer.entries.length === 0
      ? `<p id="history-empty">Todavía no hay verificaciones.</p>`
      : `<ul id="history">${answer.entries.map(entryHTML).join("")}</ul>`);
  };

  // Not awaited: `main.ts` holds the teardown only once this returns, and a
  // holder who leaves before the issuer answers has to be able to set `gone`.
  void load().catch(() =>
    paint(`<p class="warn">No se pudo cargar tu historial en este teléfono.</p>`));
  return () => {
    gone = true;
  };
}
